import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useIsMutationPending, useMutation } from "@-/com.npmjs.tanstack__react-query";
import { persistentKeyboardPreserveFocus } from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { LearnDeckContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useVoiceStudyEnabled } from "@-/dev.improve.haniverse.lib.voice-study";

// Shared mutation key so every `useRevealMutation` consumer (footer button, container
// background Pressable, shimmer overlay) can observe the same in-flight reveal
// without lifting state through context.
const REVEAL_MUTATION_KEY = ["drill", "reveal"] as const;

export function useIsRevealing() {
  const isFlipping = useContextSelectors(
    LearnDeckContext,
    (c) => c.state.transition.phase === "flipping",
  )[0];
  return useIsMutationPending(REVEAL_MUTATION_KEY) || isFlipping;
}

/**
 * Reveal the current drill: flip the card to its answer.
 *
 * The single chokepoint for EVERY drill reveal path (typed-correct auto-submit, Enter, the "I
 * forgot" shortcut, the tap-to-reveal target and hint, the reveal button, voice) — so the pinyin
 * plays once here however the card is revealed, and revealing is only ever a FLIP within the
 * deck, never a navigation. A sentence drill reveals IN PLACE instead: it force-reveals the blocked
 * word and switches into its rating state with the sentence still on screen.
 */
export function useRevealMutation() {
  const voiceEnabled = useVoiceStudyEnabled();
  const [playRevealAudio, sentenceDrill] = useContextSelectors(
    DrillPageContext,
    (c) => c.playRevealAudio,
    (c) => c.sentenceDrill,
  );
  const [flip] = useContextSelectors(LearnDeckContext, (c) => c.flip);

  return useMutation({
    mutationFn: async (animation: "swipe-up" | void) => {
      if (sentenceDrill !== null) {
        sentenceDrill.dispatch({ kind: "reveal" });
        return;
      }
      // Reveal effects run ONCE, at this transition — never because a background face mounted.
      if (!voiceEnabled) {
        playRevealAudio();
      }
      // Keep the review keyboard session alive across the flip so rating shortcuts still work
      // and the next card inherits focus.
      await persistentKeyboardPreserveFocus();
      flip("back", animation ?? undefined);
    },
    mutationKey: REVEAL_MUTATION_KEY,
  });
}
